import { useState } from "react";
import {
    Link,
    useLocation,
    useNavigate
} from "react-router-dom";
import {
    FaBuilding,
    FaShieldAlt,
    FaSignOutAlt,
    FaTachometerAlt,
    FaUserTie,
    FaBars,
    FaTimes,
    FaHistory,
    FaChartBar,
    FaUserCog
} from "react-icons/fa";

function Sidebar() {
    const navigate = useNavigate();
    const location = useLocation();
    const role = localStorage.getItem("role");
    const [isOpen, setIsOpen] = useState(false);

    // =====================================
    // LOGOUT
    // =====================================
    const logout = () => {
        const confirmLogout = window.confirm(
            "Are you sure you want to logout?"
        );
        if (confirmLogout) {
            localStorage.removeItem("token");
            localStorage.removeItem("role");
            navigate("/login");
        }
    };

    // =====================================
    // ACTIVE MENU
    // =====================================
    const isActive = (path) => {
        return location.pathname === path;
    };

    const isProfileActive = () => {
        return (
            location.pathname.startsWith("/profiles") ||
            location.pathname.startsWith("/view-profile") ||
            location.pathname.startsWith("/edit-profile") ||
            location.pathname.startsWith("/add-profile")
        );
    };

    const toggleSidebar = () => {
        setIsOpen(!isOpen);
    };

    return (
        <>
            {/* MOBILE TOGGLE BUTTON */}
            <button
                className="btn btn-dark d-md-none position-fixed shadow-lg"
                style={{
                    top: "15px",
                    left: "15px",
                    zIndex: 1100,
                    width: "45px",
                    height: "45px",
                    padding: "0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    backgroundColor: "#0f172a",
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    color: "#ffffff"
                }}
                onClick={toggleSidebar}
                aria-label="Toggle Sidebar"
            >
                {isOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
            </button>

            {/* BACKDROP OVERLAY FOR MOBILE */}
            {isOpen && (
                <div
                    className="sidebar-overlay d-md-none"
                    onClick={() => setIsOpen(false)}
                />
            )}

            {/* SIDEBAR CONTAINER */}
            <div className={`sidebar-container ${isOpen ? "open" : ""}`}>
                {/* TOP */}
                <div>
                    {/* LOGO */}
                    <div className="p-4 border-bottom border-secondary border-opacity-25">
                        <h3 className="fw-extrabold text-center text-white mb-0 d-flex flex-column align-items-center justify-content-center gap-2">
                            <img
                                src="/logo.png"
                                alt="Logo"
                                width="80"
                                height="80"
                                style={{ objectFit: "contain", borderRadius: "40%" }}
                            />
                            <span style={{ fontSize: "20px" }}>Entropy Connect</span>
                        </h3>
                        <div className="text-center mt-3">
                            <span
                                className="badge px-3 py-2 text-uppercase font-monospace"
                                style={{
                                    fontSize: "12px",
                                    letterSpacing: "1px",
                                    background: "rgba(79, 70, 229, 0.2)",
                                    border: "1px solid rgba(79, 70, 229, 0.4)",
                                    color: "#a5b4fc"
                                }}
                            >
                                <FaShieldAlt className="me-1" />
                                {role || "viewer"}
                            </span>
                        </div>
                    </div>

                    {/* MENU */}
                    <div className="p-3">
                        {/* DASHBOARD */}
                        <Link
                            to="/dashboard"
                            className={`d-flex align-items-center text-decoration-none mb-2 p-3 rounded-3 transition-all ${isActive("/dashboard")
                                ? "bg-primary text-white shadow-md fw-bold"
                                : "text-secondary hover-bg-dark text-light"
                                }`}
                            style={{ transition: "all 0.2s" }}
                            onClick={() => setIsOpen(false)}
                        >
                            <FaTachometerAlt className="me-3" size={18} />
                            Dashboard
                        </Link>

                        {/* DEPARTMENTS */}
                        {role === "admin" && (
                            <Link
                                to="/departments"
                                className={`d-flex align-items-center text-decoration-none mb-2 p-3 rounded-3 transition-all ${isActive("/departments")
                                    ? "bg-primary text-white shadow-md fw-bold"
                                    : "text-secondary hover-bg-dark text-light"
                                    }`}
                                style={{ transition: "all 0.2s" }}
                                onClick={() => setIsOpen(false)}
                            >
                                <FaBuilding className="me-3" size={18} />
                                Departments
                            </Link>
                        )}

                        {/* AUDIT LOGS (ADMIN ONLY) */}
                        {role === "admin" && (
                            <Link
                                to="/audit-logs"
                                className={`d-flex align-items-center text-decoration-none mb-2 p-3 rounded-3 transition-all ${isActive("/audit-logs")
                                    ? "bg-primary text-white shadow-md fw-bold"
                                    : "text-secondary hover-bg-dark text-light"
                                    }`}
                                style={{ transition: "all 0.2s" }}
                                onClick={() => setIsOpen(false)}
                            >
                                <FaHistory className="me-3" size={18} />
                                Audit Logs
                            </Link>
                        )}

                        {/* REPORTS (ADMIN ONLY) */}
                        {role === "admin" && (
                            <Link
                                to="/reports"
                                className={`d-flex align-items-center text-decoration-none mb-2 p-3 rounded-3 transition-all ${isActive("/reports")
                                    ? "bg-primary text-white shadow-md fw-bold"
                                    : "text-secondary hover-bg-dark text-light"
                                    }`}
                                style={{ transition: "all 0.2s" }}
                                onClick={() => setIsOpen(false)}
                            >
                                <FaChartBar className="me-3" size={18} />
                                Reports
                            </Link>
                        )}

                        {/* USER MANAGEMENT (ADMIN & MANAGER) */}
                        {(role === "admin" || role === "manager") && (
                            <Link
                                to="/users"
                                className={`d-flex align-items-center text-decoration-none mb-2 p-3 rounded-3 transition-all ${isActive("/users")
                                    ? "bg-primary text-white shadow-md fw-bold"
                                    : "text-secondary hover-bg-dark text-light"
                                    }`}
                                style={{ transition: "all 0.2s" }}
                                onClick={() => setIsOpen(false)}
                            >
                                <FaUserCog className="me-3" size={18} />
                                User Management
                            </Link>
                        )}

                        {/* PROFILES */}
                        <Link
                            to="/profiles"
                            className={`d-flex align-items-center text-decoration-none mb-2 p-3 rounded-3 transition-all ${isProfileActive()
                                ? "bg-primary text-white shadow-md fw-bold"
                                : "text-secondary hover-bg-dark text-light"
                                }`}
                            style={{ transition: "all 0.2s" }}
                            onClick={() => setIsOpen(false)}
                        >
                            <FaUserTie className="me-3" size={18} />
                            Profiles
                        </Link>
                    </div>
                </div>

                {/* FOOTER */}
                <div className="p-3 border-top border-secondary border-opacity-25">
                    <button
                        className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center py-2"
                        onClick={logout}
                        style={{ borderRadius: "8px" }}
                    >
                        <FaSignOutAlt className="me-2" />
                        Logout
                    </button>
                </div>
            </div>
        </>
    );
}

export default Sidebar;